import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useFavorites } from "@/hooks/use-favorites";
import OnboardingStep from "./OnboardingStep";

const OnboardingBanner = () => {
  const navigate = useNavigate();
  const {
    onboardingData,
    isLoading,
    organizationId,
    completedSteps,
    progressPercent,
    toggleBannerCollapsed,
  } = useOnboarding();
  const { favorites } = useFavorites();
  
  const [showCelebration, setShowCelebration] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sync collapsed state from database
  useEffect(() => {
    if (onboardingData) {
      setIsCollapsed(onboardingData.banner_collapsed);
    }
  }, [onboardingData?.banner_collapsed]);

  // Handle 100% completion celebration
  useEffect(() => {
    if (progressPercent === 100 && onboardingData && !onboardingData.onboarding_completed) {
      setShowCelebration(true);
      const timer = setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [progressPercent, onboardingData]);

  // Never show if:
  // 1. Still loading
  // 2. onboarding_completed is true
  // 3. No organization
  if (isLoading || !onboardingData || onboardingData.onboarding_completed || !organizationId) {
    return null;
  }

  const handleToggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    toggleBannerCollapsed(newState);
  };

  const handleStepClick = (step: number) => {
    switch (step) {
      case 1:
        navigate("/installningar");
        // Scroll to website section after navigation
        setTimeout(() => {
          const element = document.getElementById("website-section");
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 300);
        break;
      case 2:
        navigate("/search");
        break;
      case 3:
        navigate("/stiftelsedatabas");
        break;
      case 4:
        navigate("/manage");
        break;
    }
  };

  // Determine which step is the current/next one to highlight
  const getNextIncompleteStep = () => {
    if (!onboardingData.step_1_completed) return 1;
    if (!onboardingData.step_2_completed) return 2;
    if (!onboardingData.step_3_completed) return 3;
    if (!onboardingData.step_4_completed) return 4;
    return 0;
  };

  const nextStep = getNextIncompleteStep();
  const favoritesCount = Math.min(favorites.length, 5);

  // Celebration state
  if (showCelebration) {
    return (
      <div className="mx-4 md:mx-8 mb-4">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border border-green-200 dark:border-green-800 rounded-2xl p-6 animate-fade-in">
          <div className="flex items-center justify-center gap-3 text-green-700 dark:text-green-300">
            <PartyPopper className="h-6 w-6 animate-bounce" />
            <span className="text-lg font-semibold">
              🎉 Lycka till med dina ansökningar!
            </span>
            <PartyPopper className="h-6 w-6 animate-bounce" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 md:mx-8 mb-4">
      <Collapsible open={!isCollapsed} onOpenChange={() => handleToggleCollapsed()}>
        <div className="max-w-6xl mx-auto bg-background border border-border/50 rounded-2xl shadow-sm overflow-hidden">
          {/* Header - always visible */}
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  {isCollapsed ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </Button>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">
                    Kom igång med Bidragsassistenten
                  </h2>
                  {isCollapsed && (
                    <p className="text-xs text-muted-foreground">
                      {completedSteps}/4 steg klara
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground">
                  {progressPercent}%
                </span>
                <Progress
                  value={progressPercent}
                  className="w-24 h-2"
                />
              </div>
            </div>
          </CollapsibleTrigger>

          {/* Expandable content */}
          <CollapsibleContent>
            <div className="px-4 pb-4 pt-0">
              {/* Steps grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <OnboardingStep
                  stepNumber={1}
                  title="Generera en beskrivning"
                  subtitle="Kopiera in din hemsida för att komma igång"
                  isCompleted={onboardingData.step_1_completed}
                  isHighlighted={nextStep === 1}
                  buttonText="Starta"
                  onClick={() => handleStepClick(1)}
                />
                <OnboardingStep
                  stepNumber={2}
                  title="Hitta matchade stiftelser"
                  subtitle="Generera sökord baserat på din organisation"
                  isCompleted={onboardingData.step_2_completed}
                  isHighlighted={nextStep === 2}
                  buttonText="Sök"
                  onClick={() => handleStepClick(2)}
                />
                <OnboardingStep
                  stepNumber={3}
                  title="Favoritmarkera 5 stiftelser"
                  subtitle="Spara stiftelser du vill följa"
                  isCompleted={onboardingData.step_3_completed}
                  isHighlighted={nextStep === 3}
                  progressText={onboardingData.step_3_completed ? undefined : `${favoritesCount}/5`}
                  onClick={() => handleStepClick(3)}
                />
                <OnboardingStep
                  stepNumber={4}
                  title="Starta din första ansökan"
                  subtitle="Skapa ett AI-utkast på minuter"
                  isCompleted={onboardingData.step_4_completed}
                  isHighlighted={nextStep === 4}
                  buttonText="Skapa"
                  onClick={() => handleStepClick(4)}
                />
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

export default OnboardingBanner;
